/**
 * Removes marks the visual editor leaks into saved email HTML.
 *
 * Only exact editor fingerprints are touched, so hand-written templates stay
 * byte-for-byte the same. A tag with nothing to clean is returned untouched.
 */

import { restoreTableTokens } from './html-to-mjml';

// Background Chrome copies from the editor canvas when the list button is used.
const CANVAS_BG = /(^|;)\s*background-color\s*:\s*rgb\(\s*250\s*,\s*250\s*,\s*250\s*\)\s*;?/gi;
const POINTER_EVENTS_ALL = /(^|;)\s*pointer-events\s*:\s*all\s*;?/gi;

export function cleanEditorArtifacts(html: string): string {
  if (!html) return html;
  // Table placeholders kept as comments while editing go back to their {{token}} form.
  return restoreTableTokens(html).replace(/<([a-zA-Z][\w-]*)\b[^<>]*>/g, (tag: string, tag_name: string) =>
    cleanTag(tag, tag_name.toLowerCase()),
  );
}

function cleanTag(tag: string, tag_name: string): string {
  const is_editor_marked = /\sdraggable="true"/i.test(tag);
  let out = tag;

  out = out.replace(/\sstyle="([^"]*)"/i, (match: string, style: string) => {
    let s = style;
    // Copied grey only on spans the editor created (they carry draggable="true").
    if (tag_name === 'span' && is_editor_marked) s = s.replace(CANVAS_BG, '$1');
    s = s.replace(POINTER_EVENTS_ALL, '$1');
    if (tag_name === 'ol' || tag_name === 'ul' || tag_name === 'li') s = fixFrozenLineHeight(s);
    if (s === style) return match;

    s = s.replace(/;{2,}/g, ';').replace(/^\s*;/, '').trim();
    return s ? ` style="${s}"` : '';
  });

  // Editor-only mark on list items whose number colour follows the text (the colour itself stays).
  if (tag_name === 'li') out = out.replace(/\sdata-sz-li-color(?:="[^"]*")?/gi, '');

  return is_editor_marked ? out.replace(/\sdraggable="true"/gi, '') : out;
}

// List button froze line-height to the font size (e.g. 13px/13px) — no gap between lines.
function fixFrozenLineHeight(style: string): string {
  const line_height = style.match(/line-height\s*:\s*([\d.]+)px/i);
  const font_size = style.match(/font-size\s*:\s*([\d.]+)px/i);
  if (!line_height || !font_size) return style;
  if (parseFloat(line_height[1]) !== parseFloat(font_size[1])) return style;
  return style.replace(/line-height\s*:\s*[\d.]+px/i, 'line-height:1.5');
}
